import React from "react";
import { Form } from "react-bootstrap";
import { Box, Button, Chip, Stack, Typography } from "@mui/material";
import { DataCardShell } from "../../../../../components/responsive";
import Helpers from "../../../../../components/Helpers";
import { setBrokenImageFallback } from "../../../../../utils/imageFallback";
import Profile from "../../../../../assets/images/couple-default.png";
import corruptedImg from "../../../../../assets/images/corrupted-img.png";
import {
  canToggleAdminRights,
  coupleDisplayName,
  isInactiveCoupleStatus,
} from "../coupleDisplay";

export function CoupleCard({
  row,
  isAdmin,
  onEdit,
  onDelete,
  onStatusChange,
  onToggleSubAdmin,
  onToggleSuperAdmin,
}: {
  row: any;
  isAdmin: boolean;
  onEdit: (row: any) => void;
  onDelete: (row: any) => void;
  onStatusChange: (id: number, value: string) => void;
  onToggleSubAdmin: (value: any, row: any, adminType: string) => void;
  onToggleSuperAdmin: (value: any, row: any, adminType: string) => void;
}) {
  const inactive = isInactiveCoupleStatus(row.accountStatus);
  const statusLabel = row.accountStatus
    ? `${row.accountStatus.charAt(0).toUpperCase()}${row.accountStatus.slice(1).toLowerCase()}`
    : "—";
  const showAdminToggles =
    isAdmin &&
    row.accountStatus === "ACTIVE" &&
    (row.hisEmail || row.herEmail) &&
    canToggleAdminRights(row);

  return (
    <DataCardShell
      borderColor={inactive ? "#dfc2c4" : undefined}
      bgcolor={inactive ? "#f8d7da" : undefined}
      title={
        <Stack direction="row" spacing={1} alignItems="center">
          <Box
            component="img"
            src={row.imageUrl || Profile}
            alt=""
            onError={(e: any) => setBrokenImageFallback(e, corruptedImg)}
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              objectFit: "cover",
              border: "1px solid #e5e7eb",
              flexShrink: 0,
            }}
          />
          <span>{coupleDisplayName(row)}</span>
        </Stack>
      }
      subtitle={[
        row.primaryKey ? `ID ${row.primaryKey}` : null,
        row.parish || null,
      ]
        .filter(Boolean)
        .join(" · ")}
      chips={
        <Chip
          size="small"
          label={statusLabel}
          sx={{
            fontWeight: 700,
            bgcolor: inactive ? "#f1b0b7" : "#dcecff",
            color: inactive ? "#721c24" : "#0d5ea6",
          }}
        />
      }
      fields={[
        {
          label: "His",
          value: (
            <Box sx={{ bgcolor: "#E0F8FE", p: 1, borderRadius: 1 }}>
              <Typography variant="body2" fontWeight={700}>
                {[row.hisFirstName, row.hisLastName].filter(Boolean).join(" ") || "—"}
              </Typography>
              {row.hisEmail ? (
                <Typography variant="body2" component="a" href={`mailto:${row.hisEmail}`}>
                  {row.hisEmail}
                </Typography>
              ) : null}
              <Typography variant="body2">{row.hisMobile || "—"}</Typography>
            </Box>
          ),
        },
        {
          label: "Her",
          value: (
            <Box sx={{ bgcolor: "#FFEDFB", p: 1, borderRadius: 1 }}>
              <Typography variant="body2" fontWeight={700}>
                {[row.herFirstName, row.herLastName].filter(Boolean).join(" ") || "—"}
              </Typography>
              {row.herEmail ? (
                <Typography variant="body2" component="a" href={`mailto:${row.herEmail}`}>
                  {row.herEmail}
                </Typography>
              ) : null}
              <Typography variant="body2">{row.herMobile || "—"}</Typography>
            </Box>
          ),
        },
        { label: "Address", value: row.address || "—" },
        { label: "City", value: row.city || "—" },
        { label: "State", value: row.state || "—" },
        { label: "Zip", value: row.zip || "—" },
        {
          label: "Anniversary",
          value: row.anniversary_date
            ? Helpers.convertUtcToTimezone(row.anniversary_date, "M/D/YYYY")
            : "—",
        },
        {
          label: "Created",
          value: row.createdAt
            ? Helpers.convertUtcToTimezone(row.createdAt, "MM-DD-YYYY")
            : "—",
        },
      ]}
      primaryAction={
        isAdmin ? (
          <Button size="small" variant="contained" onClick={() => onEdit(row)}>
            Edit
          </Button>
        ) : undefined
      }
      secondaryActions={
        isAdmin ? (
          <Stack spacing={1} sx={{ width: "100%" }}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
              <Button size="small" color="error" variant="outlined" onClick={() => onDelete(row)}>
                Delete
              </Button>
              <Form.Select
                size="sm"
                value={row.accountStatus}
                onChange={(e) => onStatusChange(row.id, e.target.value)}
                className="selectData"
                style={{ width: 140 }}
              >
                <option value="ACTIVE">Active</option>
                <option value="DECEASED">Deceased</option>
                <option value="DIVORCED">Divorced</option>
                <option value="INACTIVE">Inactive</option>
              </Form.Select>
            </Stack>
            {showAdminToggles && (
              <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap alignItems="center">
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                  <Typography variant="caption" fontWeight={700} color="text.secondary">
                    Sub Admin
                  </Typography>
                  <Form.Check
                    className="switchSize"
                    type="switch"
                    checked={Boolean(row.isAdminRight)}
                    onChange={(e) => onToggleSubAdmin(e.target.value, row, "SUBADMIN")}
                    id={`card-subadmin-${row.id}`}
                  />
                </Box>
                {(row.userRole === "SUBADMIN" || row.userRole === "ADMIN") && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    <Typography variant="caption" fontWeight={700} color="text.secondary">
                      Super Admin
                    </Typography>
                    <Form.Check
                      className="switchSize"
                      type="switch"
                      checked={row.userRole === "ADMIN"}
                      onChange={(e) => onToggleSuperAdmin(e.target.value, row, "ADMIN")}
                      id={`card-admin-${row.id}`}
                    />
                  </Box>
                )}
              </Stack>
            )}
          </Stack>
        ) : undefined
      }
    />
  );
}
